import {
  Box,
  Container,
  Header,
  SpaceBetween,
  Select,
  SelectProps,
} from "@cloudscape-design/components";
import { Divider, Spin, Pagination as AntdPagination } from "antd";
import { skipToken } from "@reduxjs/toolkit/query";
import IncredableDateFormat from "../common/incredable-date-format";
import { useGetProviderProfileHistoryQuery } from "../redux/api/document/provider_document";
import { useParams } from "react-router-dom";
import { ProviderHisotryType } from "../redux/api/document/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetAllSectionQuery } from "../redux/api/section/section";

export default function ProviderProfileAuditLog() {
  const { providerId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredHistory, setFilteredHistory] = useState<ProviderHisotryType[]>(
    []
  );
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedFilter, setSelectedFilter] = useState<SelectProps.Option>({
    value: "all",
    label: "All",
  });

  const {
    data: providerHistoryList,
    isLoading,
    isFetching,
  } = useGetProviderProfileHistoryQuery(
    providerId
      ? {
          providerId,
          sectionId:
            selectedFilter.value === "all" ? undefined : selectedFilter.value,
          pageSize: itemsPerPage,
          pageNumber: currentPage,
        }
      : skipToken
  );

  const { data: sectionData, isLoading: isSectionsLoading } =
    useGetAllSectionQuery({
      isFacility: false,
    });

  useEffect(() => {
    if (providerHistoryList?.providerHistoryViewDTOS) {
      setFilteredHistory(providerHistoryList.providerHistoryViewDTOS);
    }
  }, [providerHistoryList]);

  const filterOptions = useMemo(() => {
    const options = [{ label: "All", value: "all" }];
    if (sectionData) {
      options.push(
        ...sectionData.map((section) => ({
          label: section.name,
          value: section.id,
        }))
      );
    }
    return options;
  }, [sectionData]);

  const totalItems = useMemo(
    () => providerHistoryList?.totalRecords || 0,
    [providerHistoryList]
  );

  const handlePaginationChange = useCallback(
    (page: number, pageSize?: number) => {
      setCurrentPage(page);
      if (pageSize && pageSize !== itemsPerPage) {
        setItemsPerPage(pageSize);
        setCurrentPage(1);
      }
    },
    [itemsPerPage]
  );

  const handleFilterChange = useCallback(
    ({ detail }: { detail: { selectedOption: SelectProps.Option } }) => {
      setSelectedFilter(detail.selectedOption);
      setCurrentPage(1);
    },
    []
  );

  const renderHistoryItem = useCallback(
    (providerHistory: ProviderHisotryType, index: number) => {
      return (
        <div key={index}>
          <Divider
            dashed
            style={{
              borderWidth: "1px 0 0",
              margin: "4px 0",
            }}
          />
          <Box variant="p">
            <b>{providerHistory?.user} </b> modified{" "}
            <b>
              {!!providerHistory?.attributeValues &&
                providerHistory?.attributeValues["sectionName"]}{" "}
              {providerHistory?.docVersion?.name}
            </b>{" "}
            on{" "}
            <b>
              {!!providerHistory?.createdDate
                ? new Date(providerHistory?.createdDate).toLocaleString()
                : "-"}
            </b>
          </Box>
          <Divider
            dashed
            style={{
              borderWidth: "1px 0 0",
              margin: "4px 0",
            }}
          />
          {!!providerHistory?.attributeValues && (
            <Box variant="p">
              {Object.entries(providerHistory.attributeValues)
                .filter(([key]) => key !== "sectionName")
                .map(([key, value]) => {
                  return (
                    <div key={key}>
                      {key}:{" "}
                      <i>
                        {key.toLowerCase().includes("date")
                          ? IncredableDateFormat(value)
                          : value === "false" || value === "true"
                          ? value === "true"
                            ? "Yes"
                            : "No"
                          : value || "-"}
                      </i>
                    </div>
                  );
                })}
            </Box>
          )}
        </div>
      );
    },
    []
  );

  const historyItems = useMemo(() => {
    if (!filteredHistory.length) {
      return <div>No Audit logs Found</div>;
    }

    return filteredHistory.map(renderHistoryItem);
  }, [filteredHistory, renderHistoryItem]);

  if (isLoading || isSectionsLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 275px)",
          marginBottom: "74px",
        }}
      >
        <Spin tip="Loading..." />
      </div>
    );
  }

  return (
    <div
      style={{
        height: "calc(100vh - 230px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "#fff",
          padding: "0 16px 16px 16px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <Header
          actions={
            <Select
              selectedOption={selectedFilter}
              onChange={handleFilterChange}
              options={filterOptions}
              placeholder="Choose a filter"
            />
          }
        >
          Audit Logs
        </Header>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
        }}
      >
        <Spin spinning={isFetching}>
          <Container>
            <SpaceBetween size="m">{historyItems}</SpaceBetween>
          </Container>
        </Spin>
      </div>

      {!!filteredHistory.length && (
        <div
          style={{
            padding: "16px",
            borderTop: "1px solid #ddd",
            backgroundColor: "#fff",
          }}
        >
          <AntdPagination
            current={currentPage}
            onChange={handlePaginationChange}
            pageSize={itemsPerPage}
            total={totalItems}
            showSizeChanger
            showTotal={(total) => `Total ${total} items`}
          />
        </div>
      )}
    </div>
  );
}
